import React, { useState, useEffect } from 'react';
import {
  Box, Text, Flex, Icon, Tooltip, Badge, useColorModeValue, Collapse
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

// SidebarItem Component with enhanced animation and visual effects
export const SidebarItem = ({ icon, label, to, active, hasBadge, badgeCount, isSubmenuItem = false }) => {
  const navigate = useNavigate();
  const activeBg = useColorModeValue('#4bedffff', '#4bedffff');
  const activeColor = useColorModeValue('black', 'black');
  const hoverBg = useColorModeValue('rgba(255, 214, 0, 0.2)', 'rgba(255, 214, 0, 0.3)');
  const hoverColor = useColorModeValue('black', 'black');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  
  return (
    <Tooltip label={label} placement="right" hasArrow isDisabled={!isSubmenuItem}>
      <Flex 
        align="center" 
        p={isSubmenuItem ? "2" : "4"}
        pl={isSubmenuItem ? "6" : "4"}
        mx={isSubmenuItem ? "2" : "4"} 
        borderRadius="lg" 
        role="group" 
        cursor="pointer" 
        transition="all 0.3s ease"
        bg={active ? activeBg : 'transparent'}
        color={active ? activeColor : mutedTextColor}
        _hover={{
          bg: active ? activeBg : hoverBg,
          color: active ? activeColor : hoverColor,
          transform: 'translateX(3px)',
        }}
        position="relative"
        onClick={() => to && navigate(to)}
        fontWeight={active ? "600" : "medium"}
      >
        {active && (
          <Box 
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            h="70%"
            w="4px"
            bg="#4bedffff"
            borderRightRadius="md"
            transition="all 0.2s ease"
          />
        )}
        
        <Icon 
          as={icon} 
          fontSize={isSubmenuItem ? "16px" : "20px"} 
          mr="4"
          transition="all 0.3s ease"
          _groupHover={{ transform: 'scale(1.1)' }}
        />
        
        <Text fontSize={isSubmenuItem ? "sm" : "md"}>
          {label}
        </Text>
        
        {hasBadge && badgeCount > 0 && (
          <Badge 
            colorScheme="red" 
            borderRadius="full" 
            ml="2"
            fontSize="xs"
            px="2"
            animation={badgeCount > 0 ? "pulse 2s infinite" : "none"}
          >
            {badgeCount}
          </Badge>
        )}
      </Flex>
    </Tooltip>
  );
};

// Custom Accordion Item for menu sections
export const MenuAccordionItem = ({ 
  icon, 
  label, 
  isActive, 
  children, 
  hasBadge = false, 
  badgeCount = 0,
  defaultIsOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen || isActive);
  const activeTextColor = useColorModeValue('black', 'black');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('rgba(255, 214, 0, 0.2)', 'rgba(255, 214, 0, 0.3)');
  
  // Update isOpen when active state changes
  useEffect(() => {
    if (isActive && !isOpen) {
      setIsOpen(true);
    }
  }, [isActive, isOpen]);
  
  return (
    <Box mb="1">
      <Flex
        align="center"
        p="4"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        transition="all 0.3s ease"
        onClick={() => setIsOpen(!isOpen)}
        bg={isActive ? 'rgba(255, 214, 0, 0.2)' : 'transparent'}
        color={isActive ? activeTextColor : mutedTextColor}
        _hover={{
          bg: hoverBg,
        }}
        position="relative"
      >
        {isActive && (
          <Box 
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            h="70%"
            w="4px"
            bg="#4bedffff"
            borderRightRadius="md"
          />
        )}
        
        <Icon 
          as={icon} 
          fontSize="20px" 
          mr="4"
          transition="transform 0.3s ease"
          _groupHover={{ transform: 'scale(1.1)' }}
          color={isActive ? '#4bedffff' : 'inherit'}
        />
        
        <Text 
          flex="1" 
          fontWeight={isActive ? "600" : "medium"}
          fontSize="md"
        >
          {label}
        </Text>
        
        {hasBadge && badgeCount > 0 && (
          <Badge 
            colorScheme="red" 
            borderRadius="full"
            fontSize="xs"
            px="2"
            mr="2"
          >
            {badgeCount}
          </Badge>
        )}
        
        <Icon 
          as={isOpen ? FaChevronUp : FaChevronDown} 
          transition="all 0.3s ease"
          transform={isOpen ? 'rotate(0deg)' : 'rotate(0deg)'}
          fontSize="14px"
        />
      </Flex>
      
      <Collapse in={isOpen} animateOpacity>
        <Box 
          pl="4" 
          mb="2"
          borderLeftWidth="1px"
          borderLeftColor={useColorModeValue('rgba(255, 214, 0, 0.4)', 'rgba(255, 214, 0, 0.6)')}
          ml="8"
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};